<script setup lang="ts">
import { router } from '@inertiajs/vue3'
import { ChevronDown, PhoneOutgoing } from 'lucide-vue-next'
import { ref } from 'vue'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ordinal } from '@/lib/utils'
import { trackCallLoanApplicationPath } from '@/routes'

interface Props {
  applicationId: string
  callCount: number
  showCounter?: boolean
  returnTo?: string
  className?: string
}

const props = withDefaults(defineProps<Props>(), { showCounter: true, returnTo: undefined })

const maxCalls = 5

const ALL_OPTIONS = [
  { label: 'Successfully contacted', action: 0, message: 'success' },
  { label: 'Will call us back', action: 0, message: 'back' },
  { label: 'Incorrect number', action: 1, message: 'wrong' },
  { label: 'No answer, left a message', action: 2, message: 'no' },
  { label: 'Accept application', action: 3, message: 'taken' },
]

const processing = ref(false)

function log(action: number, message: string) {
  if (processing.value) return
  processing.value = true
  router.post(
    trackCallLoanApplicationPath(props.applicationId),
    {
      action_code: action,
      message,
      call_number: props.callCount + 1,
      ...(props.returnTo ? { return_to: props.returnTo } : {}),
    },
    {
      preserveScroll: true,
      onFinish: () => { processing.value = false },
    },
  )
}
</script>

<template>
  <div :class="['my-2', props.className]">
    <div v-if="showCounter" class="text-muted-foreground mb-1 flex items-center justify-between text-xs">
      <span>Call attempts</span>
      <span class="font-semibold tabular-nums">{{ callCount }} / {{ maxCalls }}</span>
    </div>

    <DropdownMenu>
      <DropdownMenuTrigger
        :disabled="callCount >= maxCalls || processing"
        class="group flex w-full cursor-pointer select-none items-center disabled:pointer-events-none disabled:opacity-40"
      >
        <PhoneOutgoing class="mr-2 size-4 group-hover:text-primary-600" />
        <span class="mr-1 hidden whitespace-nowrap text-sm group-hover:text-primary-600 md:inline">
          {{ ordinal(callCount + 1) }} call
        </span>
        <ChevronDown class="size-4 group-hover:text-primary-600" />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" class="w-56">
        <DropdownMenuLabel class="text-muted-foreground text-xs font-normal">
          Outcome for {{ ordinal(callCount + 1) }} call
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem
            v-for="opt in ALL_OPTIONS"
            :key="opt.message"
            @click="log(opt.action, opt.message)"
          >
            {{ opt.label }}
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
</template>
